import React, { useState, useEffect } from "react";
import Button from "../Button";
import ScrollArrow from "../ScrollArrow.tsx"; // Mit .tsx Extension

export default function HeroSection() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      className="min-h-[95vh] max-h-[95vh] md:min-h-screen md:max-h-screen"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px 48px 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Video Hintergrund Layer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
        }}
      >
        <video
          key={isMobile ? "mobile" : "desktop"}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={
            isMobile
              ? "/images/hero-poster-mobile.webm"
              : "/images/hero-poster-desktop.webm"
          }
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        >
          {/* Mobile Videos (Portrait) */}
          {isMobile ? (
            <>
              <source src="/images/hero-mobile.webm" type="video/webm" />
              <source src="/images/hero-mobile.mp4" type="video/mp4" />
            </>
          ) : (
            /* Desktop Videos (Landscape) */
            <>
              <source src="/images/hero-desktop.webm" type="video/webm" />
              <source src="/images/hero-desktop.mp4" type="video/mp4" />
            </>
          )}
          {/* Fallback falls Video nicht lädt */}
          Your browser does not support the video tag.
        </video>

        {/* Optional: Dunkler Overlay für bessere Lesbarkeit */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.0))",
          }}
        />
      </div>

      {/* Content Layer (über dem Video) */}
      <div
        style={{
          maxWidth: "1600px",
          width: "100%",
          textAlign: "center",
          position: "relative",
          zIndex: 10,
        }}
        className="-translate-y-[10px] md:translate-y-0"
      >
        <div
          className="text-meta"
          style={{
            opacity: heroLoaded ? 1 : 0,
            filter: `blur(${heroLoaded ? 0 : 6}px)`,
            transform: `translateY(${heroLoaded ? 0 : -10}px)`,
            transition:
              "opacity 0.4s ease-out 0.3s, filter 0.4s ease-out 0.3s, transform 0.4s ease-out 0.3s",
          }}
        >
          <p style={{ marginBottom: "30px" }}>
            Renée Gebhardt (she/her) • Berlin <br />
            Freelance Art Director • Filmmaker • Co-Founder
          </p>
        </div>
        <h1 className="heading-hero" style={{ marginBottom: "20px" }}>
          <span
            style={{
              display: "block",
              opacity: heroLoaded ? 1 : 0,
              filter: `blur(${heroLoaded ? 0 : 10}px)`,
              transform: `translateY(${heroLoaded ? 0 : -20}px)`,
              transition:
                "opacity 0.4s ease-out, filter 0.4s ease-out, transform 0.4s ease-out",
            }}
            className="mb-[0.5em] md:mb-0"
          >
            <span className="hidden md:inline">Cannes Gold for Fridge Ads</span>
            <span className="md:hidden">
              Cannes Gold <br /> for Fridge Ads
            </span>
          </span>
          <span
            style={{
              display: "block",
              opacity: heroLoaded ? 1 : 0,
              filter: `blur(${heroLoaded ? 0 : 10}px)`,
              transform: `translateY(${heroLoaded ? 0 : -20}px)`,
              transition:
                "opacity 0.4s ease-out 0.0375s, filter 0.4s ease-out 0.0375s, transform 0.4s ease-out 0.0375s",
            }}
            className="mb-[0.5em] md:mb-0"
          >
            <span className="hidden md:inline">A documentary on BDSM</span>
            <span className="md:hidden">
              A Documentary <br /> on BDSM
            </span>
          </span>
          <span
            style={{
              display: "block",
              opacity: heroLoaded ? 1 : 0,
              filter: `blur(${heroLoaded ? 0 : 10}px)`,
              transform: `translateY(${heroLoaded ? 0 : -20}px)`,
              transition:
                "opacity 0.4s ease-out 0.075s, filter 0.4s ease-out 0.075s, transform 0.4s ease-out 0.075s",
            }}
          >
            <span className="hidden md:inline">
              Turned Church into Queer Space
            </span>
            <span className="md:hidden">
              Turned Church <br /> into Queer Space
            </span>
          </span>
        </h1>

        <div className="hero-subtext" style={{ marginBottom: "14px" }}>
          {["Figure that out"].map((text, i) => (
            <p
              key={i}
              style={{
                marginBottom: i === 2 ? "12px" : "8px",
                opacity: heroLoaded ? 1 : 0,
                filter: `blur(${heroLoaded ? 0 : 8}px)`,
                transform: `translateY(${heroLoaded ? 0 : -15}px)`,
                transition: `opacity 0.4s ease-out ${
                  0.1125 + i * 0.0375
                }s, filter 0.4s ease-out ${
                  0.1125 + i * 0.0375
                }s, transform 0.4s ease-out ${0.1125 + i * 0.0375}s`,
              }}
            >
              {text}
            </p>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: "16px",
            justifyContent: "center",
            marginTop: "35px",
            marginBottom: "35px",
            opacity: heroLoaded ? 1 : 0,
            filter: `blur(${heroLoaded ? 0 : 6}px)`,
            transform: `translateY(${heroLoaded ? 0 : -10}px)`,
            transition:
              "opacity 0.4s ease-out 0.2625s, filter 0.4s ease-out 0.2625s, transform 0.4s ease-out 0.2625s",
          }}
        ></div>

        {/* ScrollArrow Component */}
        <div
          style={{
            marginTop: "0px",
            opacity: Math.max(0, 1 - scrollY / 200),
          }}
        >
          <ScrollArrow />
        </div>
      </div>
    </section>
  );
}
